import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Search, Filter, RefreshCw, MapPin, Calendar, IndianRupee, ArrowLeft, Loader2, Leaf, TrendingUp, TrendingDown } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { getEnglishCropName } from '../translations';

interface MarketPriceRecord {
  state: string;
  district: string;
  market: string;
  commodity: string;
  variety: string;
  arrival_date: string;
  min_price: string;
  max_price: string;
  modal_price: string;
}

interface LocalMarketPrice {
  id: string;
  cropName: string;
  price: number;
  trend: 'up' | 'down' | 'stable';
  lastUpdated: any;
}

interface MarketPricesScreenProps {
  t: any;
  onBack?: () => void;
  localPrices?: LocalMarketPrice[];
}

const CACHE_KEY = 'prakruthi_mithra_market_prices';
const CACHE_TIME_KEY = 'prakruthi_mithra_market_prices_timestamp';
const REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes

export default function MarketPricesScreen({ t, onBack, localPrices = [] }: MarketPricesScreenProps) {
  const [records, setRecords] = useState<MarketPriceRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState('');

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(async () => {
      // If the query contains non-ASCII characters (like Telugu/Hindi), 
      // it might fail with the English-centric API.
      // We don't have direct access to a translator component here easily without props, 
      // but we can try to detect and warn or simply use it as-is.
      setDebouncedSearch(searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchPrices = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);

    try {
      // Build query params
      const params = new URLSearchParams();
      // Use exact values from found or common states
      if (selectedState) params.append('state', selectedState);
      if (selectedDistrict) params.append('district', selectedDistrict);
      
      // Always pass the search query to the server if it exists to ensure we find relevant records
      // across the entire dataset, not just the first 1000 returned.
      if (debouncedSearch) {
        const englishSearch = getEnglishCropName(debouncedSearch.trim());
        params.append('commodity', englishSearch);
      }
      
      const response = await fetch(`/api/market-prices?${params.toString()}`);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch from server');
      }
      const data = await response.json();
      
      if (data.records && Array.isArray(data.records)) {
        setRecords(data.records);
        // Cache data only if no active filters
        if (!selectedState && !selectedDistrict && !debouncedSearch) {
          localStorage.setItem(CACHE_KEY, JSON.stringify(data.records));
          localStorage.setItem(CACHE_TIME_KEY, Date.now().toString());
        }
      } else {
        setRecords([]);
      }
    } catch (err: any) {
      console.error('Error fetching market prices:', err);
      setError(err.message || t.errorFetchingPrices);
      
      // Load from cache on error if no filter is active
      if (!selectedState && !selectedDistrict && !debouncedSearch) {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
          setRecords(JSON.parse(cached));
        }
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedState, selectedDistrict, debouncedSearch, t]);

  useEffect(() => {
    // Try to load from cache first on initial mount
    const cachedData = localStorage.getItem(CACHE_KEY);
    const cachedTime = localStorage.getItem(CACHE_TIME_KEY);
    
    if (cachedData && cachedTime && !selectedState && !selectedDistrict && !debouncedSearch) {
      const isExpired = Date.now() - parseInt(cachedTime) > REFRESH_INTERVAL;
      setRecords(JSON.parse(cachedData));
      
      if (!isExpired) {
        setLoading(false);
      } else {
        fetchPrices();
      }
    } else {
      fetchPrices();
    }

    // Set up auto-refresh only for global view
    const interval = setInterval(() => {
      if (!selectedState && !selectedDistrict && !debouncedSearch) {
        fetchPrices(true);
      }
    }, REFRESH_INTERVAL);

    return () => clearInterval(interval);
  }, [fetchPrices, selectedState, selectedDistrict, debouncedSearch]);

  const filteredRecords = useMemo(() => {
    // Now we rely more on server-side search, but can still do a final client-side pass 
    // to search in other fields like 'market' or 'variety'
    if (!searchQuery) return records;
    
    const q = searchQuery.toLowerCase().trim();
    const englishQ = getEnglishCropName(searchQuery.trim()).toLowerCase();
    
    return records.filter(record => {
      const commodity = record.commodity?.toLowerCase() || '';
      const market = record.market?.toLowerCase() || '';
      const variety = record.variety?.toLowerCase() || '';
      const state = record.state?.toLowerCase() || '';
      
      return (commodity || '').includes(q) || 
             (commodity || '').includes(englishQ) ||
             (market || '').includes(q) || 
             (variety || '').includes(q) ||
             (state || '').includes(q);
    });
  }, [records, searchQuery]);

  // Extract unique states and districts for filters from records
  const states = useMemo(() => {
    // Standard list of Indian states in Title Case (more common for this API)
    const indianStates = [
      "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", 
      "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", 
      "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", 
      "Mizoram", "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", 
      "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", 
      "West Bengal", "Andaman and Nicobar Islands", "Chandigarh", 
      "Dadra and Nagar Haveli and Daman and Diu", "Delhi", "Jammu and Kashmir", 
      "Ladakh", "Lakshadweep", "Puducherry"
    ];
    
    // Extract actual states from current records to preserve their case
    const foundStates = Array.from(new Set(records.map(r => r.state).filter(Boolean)));
    
    // Merge: found states take priority for case, then fallback to indianStates
    const seen = new Set<string>();
    const result: string[] = [];
    
    foundStates.forEach(s => {
      const lower = s.toLowerCase();
      if (!seen.has(lower)) {
        seen.add(lower);
        result.push(s);
      }
    });
    
    indianStates.forEach(s => {
      const lower = s.toLowerCase();
      if (!seen.has(lower)) {
        seen.add(lower);
        result.push(s);
      }
    });

    return result.sort((a, b) => a.localeCompare(b));
  }, [records]);

  // Predefined major districts for common states in the app's target region
  const PREDEFINED_DISTRICTS: Record<string, string[]> = {
    "Andhra Pradesh": ["Anantapur", "Chittoor", "East Godavari", "Guntur", "Krishna", "Kurnool", "Prakasam", "Srikakulam", "Sri Potti Sriramulu Nellore", "Visakhapatnam", "Vizianagaram", "West Godavari", "Y.S.R. Kadapa"],
    "Telangana": ["Adilabad", "Bhadradri Kothagudem", "Hyderabad", "Jagtial", "Jangaon", "Jayashankar Bhupalpally", "Jogulamba Gadwal", "Kamareddy", "Karimnagar", "Khammam", "Kumuram Bheem Asifabad", "Mahabubabad", "Mahabubnagar", "Mancherial", "Medak", "Medchal-Malkajgiri", "Mulugu", "Nagarkurnool", "Nalgonda", "Narayanpet", "Nirmal", "Nizamabad", "Peddapalli", "Rajanna Sircilla", "Rangareddy", "Sangareddy", "Siddipet", "Suryapet", "Vikarabad", "Wanaparthy", "Warangal", "Yadadri Bhuvanagiri"],
    "Karnataka": ["Bagalkot", "Ballari", "Belagavi", "Bengaluru Rural", "Bengaluru Urban", "Bidar", "Chamarajanagar", "Chikkaballapur", "Chikkamagaluru", "Chitradurga", "Dakshina Kannada", "Davanagere", "Dharwad", "Gadag", "Hassan", "Haveri", "Kalaburagi", "Kodagu", "Kolar", "Koppal", "Mandya", "Mysuru", "Raichur", "Ramanagara", "Shivamogga", "Tumakuru", "Udupi", "Uttara Kannada", "Vijayapura", "Yadgir"],
    "Tamil Nadu": ["Ariyalur", "Chengalpattu", "Chennai", "Coimbatore", "Cuddalore", "Dharmapuri", "Dindigul", "Erode", "Kallakurichi", "Kancheepuram", "Kanyakumari", "Karur", "Krishnagiri", "Madurai", "Mayiladuthurai", "Nagapattinam", "Namakkal", "Nilgiris", "Perambalur", "Pudukkottai", "Ramanathapuram", "Ranipet", "Salem", "Sivaganga", "Tenkasi", "Thanjavur", "Theni", "Thoothukudi", "Tiruchirappalli", "Tirunelveli", "Tirupathur", "Tiruppur", "Tiruvallur", "Tiruvannamalai", "Tiruvarur", "Vellore", "Viluppuram", "Virudhunagar"],
    "Maharashtra": ["Ahmednagar", "Akola", "Amravati", "Aurangabad", "Beed", "Bhandara", "Buldhana", "Chandrapur", "Dhule", "Gadchiroli", "Gondia", "Hingoli", "Jalgaon", "Jalna", "Kolhapur", "Latur", "Mumbai", "Nagpur", "Nanded", "Nandurbar", "Nashik", "Osmanabad", "Palghar", "Parbhani", "Pune", "Raigad", "Ratnagiri", "Sangli", "Satara", "Sindhudurg", "Solapur", "Thane", "Wardha", "Washim", "Yavatmal"],
    "Gujarat": ["Ahmedabad", "Amreli", "Anand", "Aravalli", "Banaskantha", "Bharuch", "Bhavnagar", "Botad", "Chhota Udepur", "Dahod", "Dang", "Devbhumi Dwarka", "Gandhinagar", "Gir Somnath", "Jamnagar", "Junagadh", "Kheda", "Kutch", "Mahisagar", "Mehsana", "Morbi", "Narmada", "Navsari", "Panchmahal", "Patan", "Porbandar", "Rajkot", "Sabarkantha", "Surat", "Surendranagar", "Tapi", "Vadodara", "Valsad"]
  };

  const COMMON_CROPS = t.commonCrops || [
    "Paddy", "Cotton", "Groundnut", "Maize", "Chilli", "Tomato", "Onion", 
    "Bengal Gram", "Turmeric", "Red Gram", "Green Gram", "Black Gram", 
    "Sunflower", "Soyabean", "Wheat", "Sugarcane", "Potato", "Banana"
  ];

  const formatPrice = (price: string | number) => {
    const num = Number(price);
    if (!price || isNaN(num) || num === 0) return 'N/A';
    return num.toLocaleString();
  };

  const districts = useMemo(() => {
    if (!selectedState) return [];
    
    // Get districts from current records for this state
    const foundDistricts = Array.from(new Set(
      records
        .filter(r => (r.state || '').toLowerCase() === selectedState.toLowerCase())
        .map(r => r.district)
        .filter(Boolean)
    ));

    // Get predefined districts for this state if available (case-insensitive lookup)
    const stateKey = Object.keys(PREDEFINED_DISTRICTS).find(
      key => key.toLowerCase() === selectedState.toLowerCase()
    );
    const predefined = stateKey ? PREDEFINED_DISTRICTS[stateKey] : [];
    
    // Merge and deduplicate
    const seen = new Set<string>();
    const result: string[] = [];
    
    // Found districts take precedence for casing
    [...foundDistricts, ...predefined].forEach(d => {
      const lower = d.toLowerCase().trim();
      if (!seen.has(lower)) {
        seen.add(lower);
        result.push(d.trim());
      }
    });

    return result.sort((a, b) => a.localeCompare(b));
  }, [records, selectedState]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-8">
        <motion.div
          animate={{ scale: [1, 1.2, 1], rotate: [0, 10, -10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
          className="text-[#2e7d32] mb-4"
        >
          <Leaf size={64} fill="currentColor" />
        </motion.div>
        <p className="text-[#1b5e20] font-bold text-lg animate-pulse">{t.fetchingPrices}</p>
      </div>
    );
  }

  return (
    <div className="bg-[#f1f8e9] min-h-screen">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-[#1b5e20] text-white p-4 shadow-md flex items-center gap-4">
        {onBack && (
          <button onClick={onBack} className="p-1 hover:bg-white/10 rounded-full transition-colors">
            <ArrowLeft size={24} />
          </button>
        )}
        <h1 className="text-xl font-bold flex-1">{t.liveMarketPrices}</h1>
        <button 
          onClick={() => fetchPrices(true)} 
          disabled={refreshing}
          className={`p-2 rounded-full hover:bg-white/10 transition-colors ${refreshing ? 'animate-spin' : ''}`}
        >
          <RefreshCw size={20} />
        </button>
      </div>

      <div className="p-4 space-y-4">
        {/* Search and Filters removed as per user request */}


        {error && (
          <div className="bg-red-50 border border-red-100 p-4 rounded-2xl flex items-start gap-3">
            <div className="p-2 bg-red-100 rounded-lg text-red-600">
              <Loader2 size={20} />
            </div>
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {refreshing && (
          <div className="flex items-center justify-center gap-2 text-[#1b5e20] text-xs font-bold py-2 bg-[#e8f5e9] rounded-xl animate-pulse">
            <RefreshCw size={14} className="animate-spin" />
            {t.refreshing}
          </div>
        )}

        {/* Result Tabs */}
        {localPrices.length > 0 && !selectedState && !selectedDistrict && !searchQuery && (
          <div className="space-y-4">
            <h3 className="text-sm font-bold text-stone-600 px-2 flex items-center gap-2">
              <Leaf size={16} className="text-[#1b5e20]" />
              Verified Local Prices
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {localPrices.map((price) => (
                <div key={price.id} className="bg-gradient-to-br from-[#1b5e20] to-[#2e7d32] rounded-2xl p-4 text-white shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] font-bold uppercase tracking-wider opacity-80">{price.cropName}</span>
                    {price.trend === 'down' ? (
                      <TrendingDown size={14} className="text-rose-300" />
                    ) : (
                      <TrendingUp size={14} className={price.trend === 'up' ? 'text-emerald-300' : 'text-white/60'} />
                    )}
                  </div>
                  <div className="flex items-baseline gap-1">
                    <span className="text-sm">₹</span>
                    <span className="text-xl font-black">{price.price.toLocaleString()}</span>
                    <span className="text-[10px] opacity-80">/ quintal</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="h-px bg-stone-200 mx-2 my-2" />
          </div>
        )}

        {/* Results List */}
        <div className="space-y-4">
          <AnimatePresence mode="popLayout">
            {filteredRecords.length > 0 ? (
              filteredRecords.map((record, index) => (
                <motion.div
                  key={`${record.market}-${record.commodity}-${record.variety}-${index}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white rounded-2xl p-4 shadow-sm border border-stone-100 hover:shadow-md transition-shadow"
                >
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="font-bold text-stone-800 text-lg leading-tight">{record.commodity}</h3>
                      <p className="text-xs text-stone-500 italic mt-0.5">{record.variety}</p>
                    </div>
                    <div className="bg-[#e8f5e9] text-[#1b5e20] px-2.5 py-1 rounded-full text-[10px] font-bold flex items-center gap-1">
                      <Calendar size={10} />
                      {record.arrival_date}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="space-y-2">
                       <div className="flex items-center gap-2 text-stone-500">
                        <MapPin size={14} />
                        <span className="text-xs font-medium">{record.market}, {record.district}</span>
                      </div>
                      <div className="text-[10px] text-stone-400 uppercase tracking-wider font-bold">
                        {record.state}
                      </div>
                    </div>
                    
                    <div className="bg-[#f1f8e9] rounded-xl p-3 flex flex-col items-center justify-center">
                      <span className="text-[10px] text-stone-500 uppercase font-bold mb-1">{t.modalPrice}</span>
                      <div className="flex items-center text-[#1b5e20] font-black text-xl">
                        {formatPrice(record.modal_price) !== 'N/A' && <IndianRupee size={16} strokeWidth={3} />}
                        {formatPrice(record.modal_price)}
                      </div>
                      <span className="text-[10px] text-stone-400 font-medium">/ 100 kg (Quintal)</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-3 border-t border-stone-50">
                    <div className="flex flex-col">
                      <span className="text-[10px] text-stone-400 font-bold uppercase">{t.minPrice}</span>
                      <div className="flex items-center text-stone-600 font-bold text-sm">
                        {formatPrice(record.min_price) !== 'N/A' && <IndianRupee size={12} />}
                        {formatPrice(record.min_price)}
                      </div>
                    </div>
                    <div className="flex flex-col items-end">
                      <span className="text-[10px] text-stone-400 font-bold uppercase">{t.maxPrice}</span>
                      <div className="flex items-center text-stone-600 font-bold text-sm">
                        {formatPrice(record.max_price) !== 'N/A' && <IndianRupee size={12} />}
                        {formatPrice(record.max_price)}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))
            ) : null}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
